'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { Button } from '@/components/ui/Button/button';
import { SkeletonParent } from '@/components/ui/Skeleton/skeleton';
import { Link, usePathname } from '@/i18n/routing';
import openModal from '@/modals/utils/openModal';
import avatar from '@/public/assets/image/profile/profile-avatar.webp';
import { useHasActionableVerification } from '@/queries/verifications/verifications.hooks';
import { useWalletMeQuery } from '@/queries/wallet/wallet.queries';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { checkToken } from '@/redux/slices/userSlice';
import BalancesPopover from './components/BalancesPopover';
import { useSmartBack } from './utils/handleBack';

// Root pages keep the logo on mobile; every other page shows the back arrow.
const LOGO_ONLY_PATHS = ['/', '/restricted-region'];

interface Props {
  isOpenSideBar: boolean;
  setIsOpenSideBar: (isOpen: boolean) => void;
}

export const Header = ({ isOpenSideBar, setIsOpenSideBar }: Props) => {
  const t = useTranslations('header');
  const [isBalancesOpen, setIsBalancesOpen] = useState(false);

  const pathname = usePathname();
  // On mobile the back arrow replaces the logo on every inner page. Only roots keep
  // the logo: the home/lobby and the region-block page.
  const showBackArrow = !LOGO_ONLY_PATHS.includes(pathname);

  const dispatch = useAppDispatch();
  const userSession = useAppSelector(state => state.user.userSession);
  const showAttentionIcon = useHasActionableVerification();

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleClickProfile = () => {
    if (isOpenSideBar) {
      setIsOpenSideBar(false);
    }
  };

  const handleOpenSideBar = () => {
    if (pathname === '/' && isOpenSideBar) {
      setIsOpenSideBar(false);
    }
  };

  const balancesPopoverClose = () => {
    setIsBalancesOpen(false);
  };

  const { data: coins } = useWalletMeQuery();

  const handleBack = useSmartBack();

  useEffect(() => {
    dispatch(checkToken());
  }, []);

  return (
    <header className="fixed 3xl:px-0 top-0 left-0 right-0 flex bg-[linear-gradient(195deg,_#4c2959_0%,_#231333_125.12%)] border-b border-main-800 bg-size-[200%] z-40 py-2.5 sm:py-2.5 px-4 lg:pl-5.5 lg:pr-10 h-16">
      <div className="flex 3xl:max-w-388.5 3xl:mx-auto justify-between items-center w-full">
        {showBackArrow
          ? (
              <div className="items-center bg-main-800 rounded-full p-3 lg:hidden size-11 flex justify-center">
                <button type="button" onClick={handleBack} className="flex items-center">
                  <Icon name="arrow-back" className="size-4" />
                </button>
              </div>
            )
          : (
              <Link href="/" className="block lg:hidden" onClick={handleOpenSideBar}>
                <Icon name="mobile-logo" className="w-12 h-10.5" />
              </Link>
            )}

        <Link href="/" className="max-lg:hidden">
          <Icon name="desktop-logo" className="w-35 h-11" />
        </Link>

        {!mounted
          ? (
              <div className="flex gap-2 items-center">
                <SkeletonParent className="w-23.25 h-11 rounded-[3.125rem]" />
                <SkeletonParent className="w-23.25 h-11 rounded-[3.125rem]" />
              </div>
            )
          : (
              <>
                {userSession
                  ? (
                      <>
                        <div className="flex items-center h-11 bg-c900 rounded-full">
                          <BalancesPopover
                            isBalancesOpen={isBalancesOpen}
                            setIsBalancesOpen={setIsBalancesOpen}
                            coins={coins}
                            balancesPopoverClose={balancesPopoverClose}
                          />

                          <Button
                            variant="primary"
                            size="sm"
                            className="p-3.75 rounded-[6.25rem]"
                            onClick={() => openModal('deposit')}
                          >
                            <Icon name="plus" className="size-3.5" />
                          </Button>
                        </div>
                        <Link onClick={handleClickProfile} className="cursor-pointer ransition-all duration-300" href="/profile" prefetch={false}>
                          <div className="relative size-11 hover:scale-105 duration-300">
                            <div className="size-11 overflow-hidden rounded-[6.25rem] bg-c900">
                              <Image src={avatar} alt="avatar" className="size-11" width={88} height={88} />
                            </div>
                            {showAttentionIcon && (
                              <div className="absolute bottom-0 -left-0.5 pr-0.5 -ml-px">
                                <Icon name="attention" className="size-4 min-w-4 min-h-4 rounded-full shadow-[0_0_0_3px_#361D44]" />
                              </div>
                            )}
                          </div>
                        </Link>
                      </>
                    )
                  : (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="stroked"
                          className="px-6 py-3 text-sm leading-5 font-medium text-white"
                          onClick={() => openModal('login')}
                        >
                          {t('sign-in')}
                        </Button>

                        <Button
                          variant="primary"
                          className=" py-3 px-6 text-sm leading-5 font-medium min-w-24.75"
                          onClick={() => openModal('register')}
                        >
                          {t('sign-up')}
                        </Button>
                      </div>
                    )}
              </>
            )}
      </div>
    </header>
  );
};

export default Header;
